import React, { useState, useEffect } from 'react';
import {
  Card,
  CardHeader,
  CardBody,
  CardTitle,
  Button,
  Row,
  Col,
  Alert,
  Input,
  Label,
  FormGroup,
} from 'reactstrap';
import { permisosService } from '../../services/permisosService';

// Módulos comunes del sistema
const MODULOS_DISPONIBLES = [
  'Configuración',
  'Empresas',
  'Sucursales',
  'Productos',
  'Categorías',
  'Inventario',
  'Ventas',
  'Facturación',
  'Clientes',
  'Usuarios',
  'Roles',
  'Permisos',
  'Reportes',
  'Auditoría',
];

// Acciones comunes
const ACCIONES_DISPONIBLES = [
  'Ver',
  'Crear',
  'Editar',
  'Eliminar',
  'Listar',
  'Exportar',
  'Imprimir',
];

function PermisoForm({ permiso, onSuccess, onCancel }) {
  const [formData, setFormData] = useState({
    Nombre: '',
    Descripcion: '',
    Modulo: '',
    Accion: '',
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (permiso) {
      setFormData({
        Nombre: permiso.Nombre || permiso.nombre || '',
        Descripcion: permiso.Descripcion || permiso.descripcion || '',
        Modulo: permiso.Modulo || permiso.modulo || '',
        Accion: permiso.Accion || permiso.accion || '',
      });
    }
  }, [permiso]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value,
    }));

    // Si cambia el módulo o la acción, actualizar el nombre automáticamente si está vacío o es el valor por defecto
    if ((name === 'Modulo' || name === 'Accion') && !permiso) {
      const nuevoModulo = name === 'Modulo' ? value : formData.Modulo;
      const nuevaAccion = name === 'Accion' ? value : formData.Accion;
      if (nuevoModulo && nuevaAccion && (!formData.Nombre || formData.Nombre === `${formData.Modulo} - ${formData.Accion}`)) {
        setFormData(prev => ({
          ...prev,
          Nombre: `${nuevoModulo} - ${nuevaAccion}`,
        }));
      }
    }
  };

  const validate = () => {
    if (!formData.Nombre.trim()) {
      setError('El nombre es requerido');
      return false;
    }

    if (!formData.Modulo.trim()) {
      setError('El módulo es requerido');
      return false;
    }

    if (!formData.Accion.trim()) {
      setError('La acción es requerida');
      return false;
    }

    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!validate()) {
      return;
    }

    try {
      setLoading(true);

      const permisoData = {
        Nombre: formData.Nombre.trim(),
        Descripcion: formData.Descripcion.trim() || null,
        Modulo: formData.Modulo.trim(),
        Accion: formData.Accion.trim(),
      };

      let response;
      if (permiso) {
        response = await permisosService.update(permiso.PermisoID || permiso.permisoID, permisoData);
      } else {
        response = await permisosService.create(permisoData);
      }

      if (response.success) {
        onSuccess();
      } else {
        setError(response.Message || response.message || `Error al ${permiso ? 'actualizar' : 'crear'} el permiso`);
      }
    } catch (err) {
      setError(err.message || 'Error al conectar con el servidor');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="content">
      <Row>
        <Col md="12">
          <Card>
            <CardHeader>
              <CardTitle tag="h4">
                {permiso ? 'Editar Permiso' : 'Nuevo Permiso'}
              </CardTitle>
            </CardHeader>
            <CardBody>
              {error && (
                <Alert color="danger" className="mb-3">
                  {error}
                </Alert>
              )}

              <form onSubmit={handleSubmit}>
                <Row>
                  <Col md="6">
                    <FormGroup>
                      <Label>Nombre *</Label>
                      <Input
                        type="text"
                        name="Nombre"
                        value={formData.Nombre}
                        onChange={handleChange}
                        required
                        placeholder="Ej: Productos - Crear"
                      />
                    </FormGroup>
                  </Col>
                  <Col md="6">
                    <FormGroup>
                      <Label>Módulo *</Label>
                      <Input
                        type="select"
                        name="Modulo"
                        value={formData.Modulo}
                        onChange={handleChange}
                        required
                      >
                        <option value="">Seleccione un módulo</option>
                        {MODULOS_DISPONIBLES.map((modulo) => (
                          <option key={modulo} value={modulo}>
                            {modulo}
                          </option>
                        ))}
                        <option value="Otro">Otro (especificar)</option>
                      </Input>
                      {formData.Modulo === 'Otro' && (
                        <Input
                          type="text"
                          name="Modulo"
                          value={formData.Modulo}
                          onChange={handleChange}
                          placeholder="Especifique el módulo"
                          className="mt-2"
                        />
                      )}
                    </FormGroup>
                  </Col>
                </Row>

                <Row>
                  <Col md="6">
                    <FormGroup>
                      <Label>Acción *</Label>
                      <Input
                        type="select"
                        name="Accion"
                        value={formData.Accion}
                        onChange={handleChange}
                        required
                      >
                        <option value="">Seleccione una acción</option>
                        {ACCIONES_DISPONIBLES.map((accion) => (
                          <option key={accion} value={accion}>
                            {accion}
                          </option>
                        ))}
                        <option value="Otro">Otro (especificar)</option>
                      </Input>
                      {formData.Accion === 'Otro' && (
                        <Input
                          type="text"
                          name="Accion"
                          value={formData.Accion}
                          onChange={handleChange}
                          placeholder="Especifique la acción"
                          className="mt-2"
                        />
                      )}
                    </FormGroup>
                  </Col>
                  <Col md="6">
                    <FormGroup>
                      <Label>Descripción</Label>
                      <Input
                        type="textarea"
                        name="Descripcion"
                        value={formData.Descripcion}
                        onChange={handleChange}
                        rows="3"
                        placeholder="Descripción opcional del permiso"
                      />
                    </FormGroup>
                  </Col>
                </Row>

                <Row>
                  <Col md="12" className="text-right">
                    <Button color="secondary" onClick={onCancel} className="mr-2">
                      Cancelar
                    </Button>
                    <Button color="primary" type="submit" disabled={loading}>
                      {loading ? 'Guardando...' : 'Guardar'}
                    </Button>
                  </Col>
                </Row>
              </form>
            </CardBody>
          </Card>
        </Col>
      </Row>
    </div>
  );
}

export default PermisoForm;

